import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Upload } from "lucide-react";
import { useEffect, useState } from "react";

import type { PercentileDist } from "@/components/exam/exam-histogram-graph-view";
import { ExamReportCardsList } from "@/components/exam/exam-report-cards-list";
import { ExamStatistics } from "@/components/exam/exam-statistics";
import { ExamStatsBandChart } from "@/components/exam/exam-stats-band-chart";
import { ExamStatsFlat } from "@/components/exam/exam-stats-flat";
import { ExamStatsRadarChart } from "@/components/exam/exam-stats-radar-chart";
import { ModernExamHeader } from "@/components/exam/modern-exam-header";
import type { SubjectAggStat } from "@/components/report-card/exam-stats-carousel";
import { ExamTypeOptions } from "@/lib/exam-types";
import { currentRole, currentStudentId, pb } from "@/lib/pb";
import {
  type ExamRefRecord,
  type ExpandedReportCard,
  type ReportCardCombined,
  convertRCCtoRCExpandedForExam,
  convertRCCtoRCExpandedForExamWithStudent,
  populateReportCardWithMarkFields,
  populateReportCardsWithMarkFields,
} from "@/lib/report-card-helpers";
import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * Ported from portal-ui/app/(pages)/exams/[examId]/page.tsx +
 * components/exam/exam-page.tsx.
 *
 * Layout matches portal-ui:
 *   ModernExamHeader (5-stat row + teacher chips)
 *   ExamStatistics  (YourResult? + Distribution + Leaders)
 *   ExamStatsCarouselView (MODEL exams only — avg/best/worst per subject)
 *   ExamReportCardsList (paginated, searchable)
 *
 * Deferred: elaborate per-card RC component (custom/report-card.tsx,
 * 686 LOC) — list tiles are functionally equivalent meanwhile.
 */
interface TeacherRecord {
  id: string;
  name: string;
  subjects: string[];
  qualifications?: string;
  batches: string[];
  login?: string;
}

function examQuery(examId: string, authId: string | undefined) {
  return {
    queryKey: ["exam", examId, authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("exam").getOne<ExamRefRecord>(examId, {
        fields: "id,name,type,date,total_marks,total_questions,total_required,batch",
      }),
  };
}

function topRcsQuery(examId: string, authId: string | undefined) {
  return {
    queryKey: ["exam", examId, "top-rcs", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("report_card_combined").getList<ReportCardCombined>(1, 2, {
        filter: `exam_id = '${examId}'`,
        sort: "rank",
      }),
  };
}

function markExamStatsQuery(examId: string, examType: string, authId: string | undefined) {
  return {
    queryKey: ["exam", examId, "mark-exam-stats", authId ?? "anon"] as const,
    queryFn: async (): Promise<SubjectAggStat[]> => {
      if (examType !== ExamTypeOptions.MODEL) return [];
      const page = await pb.collection("mark_exam_stats").getList<SubjectAggStat>(1, 10, {
        filter: `exam = '${examId}'`,
        skipTotal: true,
        fields: "type,avg,max,min",
      });
      return page.items;
    },
  };
}

function pDistQuery(examId: string, examType: string, authId: string | undefined) {
  return {
    queryKey: ["exam", examId, "pdist", examType, authId ?? "anon"] as const,
    queryFn: async () => {
      const dumpType = examType === ExamTypeOptions.MODEL ? "TOTAL" : examType.toUpperCase();
      try {
        return await pb
          .collection("exam_pdist_percentile_dump")
          .getFirstListItem<PercentileDist & { exam: string; type: string }>(
            `exam = '${examId}' && type = '${dumpType}'`,
          );
      } catch {
        return null;
      }
    },
  };
}

export const Route = createFileRoute("/_app/exams/$examId")({
  loader: ({ context, params }) => {
    const authId = pb.authStore.model?.id;
    return Promise.all([
      context.queryClient.ensureQueryData(examQuery(params.examId, authId)),
      context.queryClient.ensureQueryData(topRcsQuery(params.examId, authId)),
    ]);
  },
  component: ExamDetailPage,
});

function ExamDetailPage() {
  const { examId } = Route.useParams();
  const authId = pb.authStore.model?.id;

  const { data: exam } = useSuspenseQuery(examQuery(examId, authId));
  const { data: topRcs } = useSuspenseQuery(topRcsQuery(examId, authId));
  const { data: pDist } = useSuspenseQuery(pDistQuery(examId, exam.type, authId));
  // Straight from mark_exam_stats — biology is a native BIO row since the
  // 2026-07 data migration (no BOT/ZOO rows exist to fold).
  const { data: examStats } = useSuspenseQuery(markExamStatsQuery(examId, exam.type, authId));

  useDocumentTitle(exam.name);

  const [teachers, setTeachers] = useState<TeacherRecord[]>([]);
  useEffect(() => {
    let cancelled = false;
    // exam.type is never BOT/ZOO anymore, but TEACHERS still hold those
    // subjects — a Biology exam belongs to Botany and Zoology faculty too.
    const bioSubjects = [ExamTypeOptions.BIO, ExamTypeOptions.BOT, ExamTypeOptions.ZOO];
    const filter =
      exam.type === ExamTypeOptions.BIO
        ? bioSubjects.map((s) => `subjects ?~ '${s}'`).join(" || ")
        : `subjects ?~ '${exam.type}'`;
    pb.collection("teacher")
      .getList<TeacherRecord>(1, 10, { filter, skipTotal: true })
      .then((page) => {
        if (!cancelled) setTeachers(page.items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [exam.type]);

  const [studentRc, setStudentRc] = useState<ExpandedReportCard | undefined>(undefined);
  const viewerIsStudent = currentRole() === "student";
  useEffect(() => {
    // RCC.student_id is the student record id — use auth.student_id, NOT the
    // auth id (pb.authStore.model.id), or the filter never matches and the
    // student's "Your result" card silently stays empty.
    const studentId = currentStudentId();
    if (!viewerIsStudent || !studentId) return;
    let cancelled = false;
    pb.collection("report_card_combined")
      .getFirstListItem<ReportCardCombined>(`exam_id = '${examId}' && student_id = '${studentId}'`)
      .then((rcc) => {
        if (cancelled) return;
        const rc = populateReportCardWithMarkFields(
          convertRCCtoRCExpandedForExamWithStudent(rcc, exam),
        );
        setStudentRc(rc);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [examId, exam, viewerIsStudent]);

  const reportCardsForStats: ExpandedReportCard[] = populateReportCardsWithMarkFields(
    convertRCCtoRCExpandedForExam(topRcs.items, exam),
  );

  // Students receive only their own RCC row, so topRcs.totalItems collapses to
  // 1. Derive the true cohort size from the batch-visible distribution: each
  // pdist band counts the students in a percentile range and the five bands
  // together cover the whole batch. Non-students keep the exact RC total.
  const pDistCohort = pDist
    ? pDist.p0_p25 + pDist.p25_p50 + pDist.p50_p75 + pDist.p75_p90 + pDist.p90_p100
    : 0;
  const totalReportCards = viewerIsStudent && pDistCohort > 0 ? pDistCohort : topRcs.totalItems;
  // The stats block (Your Result + Distribution) should still render for a
  // student even when their own RC list is empty, as long as there's a
  // distribution or a self RC to show. Non-students gate on the RC list.
  const showStatsBlock = viewerIsStudent
    ? Boolean(pDist || studentRc)
    : reportCardsForStats.length !== 0;

  const isSupport = currentRole() === "support";

  return (
    <div className="flex flex-col gap-6">
      <ModernExamHeader exam={exam} teachers={teachers} nofReportCards={totalReportCards} />

      {isSupport && exam.batch && (
        <div className="flex justify-end">
          <Link
            to="/dashboard-upload"
            search={{ entity: "reportcard", batch: exam.batch, exam: exam.id }}
            className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-surface-muted/60 px-3 py-1.5 text-xs font-medium transition-colors hover:border-border hover:bg-surface-muted"
          >
            <Upload className="h-3.5 w-3.5" />
            Upload report cards for this exam
          </Link>
        </div>
      )}

      {showStatsBlock && (
        <>
          <ExamStatistics
            exam={exam}
            optionalStudentRc={studentRc}
            reportCards={reportCardsForStats}
            pDistData={pDist ?? undefined}
            totalReportCards={totalReportCards}
            viewerIsStudent={viewerIsStudent}
          />
          {exam.type === ExamTypeOptions.MODEL && examStats.length > 0 && (
            <>
              <ExamStatsFlat stats={examStats} />
              <ExamStatsRadarChart stats={examStats} />
              <ExamStatsBandChart stats={examStats} />
            </>
          )}
          {/* Class report cards for this exam — visible to students too (they
              may see batch-mates' report cards). The bulk CSV export inside is
              gated to non-students; profile links from here stay inert. */}
          <ExamReportCardsList exam={exam} />
        </>
      )}
    </div>
  );
}
